import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Timestamp } from "firebase/firestore"; // Import Timestamp
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import useSendSchoolNotification from "../../../../config/sendSchoolNotificationHook"; // Import notification hook
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const CourseProctorForm = ({ schoolId, pathwayEmail, opEmail }) => {
  // Use Firestore hook to add data to 'CourseProctorApp' collection
  const { addData: addFirestoreData } = useFirestore("CourseProctorApp");
  // Use notification hook
  const {
    sendNotification,
    loading: notificationLoading,
    error: notificationError,
  } = useSendSchoolNotification();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [formData, setFormData] = useState({
    fromEmail: "",
    fromName: "",
    schoolName: "",
    name: "",
    phone: "",
    email: "",
    schoolId: schoolId,
    pathwayEmail: pathwayEmail || "", // Initialize with prop values
    opEmail: opEmail || "",
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      pathwayEmail: pathwayEmail || "", // Update if props change
      opEmail: opEmail || "",
    }));
  }, [pathwayEmail, opEmail]); // Add to dependency array

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.fromName || formData.fromName.trim() === "") {
      setSnackbarMessage("From Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.schoolName || formData.schoolName.trim() === "") {
      setSnackbarMessage("School Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.name || formData.name.trim() === "") {
      setSnackbarMessage("Proctor Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarMessage(""); // Clear previous messages

    const emailBodyContent = [
      `<strong>School Name:</strong> ${formData.schoolName || "N/A"}`,
      `<strong>From Name:</strong> ${formData.fromName || "N/A"}`,
      `<strong>From Email:</strong> ${formData.fromEmail || "N/A"}`,
      `<strong>Proctor Name:</strong> ${formData.name || "N/A"}`,
      `<strong>Phone:</strong> ${formData.phone || "N/A"}`,
      `<strong>Email:</strong> ${formData.email || "N/A"}`,
    ].join("<br />");

    // Prepare data for Firestore (CourseProctorApp collection)
    const dataForFirestore = {
      ...formData,
      Date_Submitted: Timestamp.now(), // Use Date_Submitted to match admin grid field
      created_at: Timestamp.now(), // Keep for compatibility
    };

    // Prepare data for the notification
    const dataForNotification = {
      pathwayEmail:
        formData.pathwayEmail || pathwayEmail || "ccplus@sinclair.edu",
      opEmail: formData.opEmail || opEmail || "ccplus@sinclair.edu",
      schoolId: formData.schoolId || schoolId || "1",
      fromName: formData.fromName.trim(),
      schoolName: formData.schoolName.trim(),
      emailBodyContent: emailBodyContent,
    };

    try {
      // 1. Add data to Firestore CourseProctorApp collection
      await addFirestoreData(dataForFirestore);

      // 2. Send notification email
      await sendNotification(
        dataForNotification,
        "Course Proctor Application",
        "CourseProctor"
      );

      setLoading(false);
      setSnackbarMessage(
        "Course proctor application submitted and notification sent successfully!"
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      const message = error.message || "An error occurred during submission.";
      setLoading(false);
      setSnackbarMessage(`Error submitting application: ${message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setFormData({
      fromEmail: "",
      fromName: "",
      schoolName: "",
      name: "",
      phone: "",
      email: "",
      schoolId: schoolId,
      pathwayEmail: pathwayEmail || "", // Keep emails on close
      opEmail: opEmail || "", // Keep emails on close
    });
  };

  return (
    <div>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        onClick={handleClickOpen}
        sx={{ backgroundColor: "teal", color: "white" }}
      >
        Course Proctor App
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <ModalDialogTitle onClose={handleClose}>
          Course Proctor App
        </ModalDialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="School Name"
                    name="schoolName"
                    fullWidth
                    required
                    value={formData.schoolName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="From Name"
                    name="fromName"
                    fullWidth
                    required
                    value={formData.fromName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="From Email"
                    name="fromEmail"
                    fullWidth
                    value={formData.fromEmail}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Proctor Name"
                    name="name"
                    fullWidth
                    required
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Proctor Phone"
                    name="phone"
                    fullWidth
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Proctor Email"
                    name="email"
                    fullWidth
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} saveLabel="Submit" />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ width: "100%" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            backgroundColor: snackbarSeverity === "success" ? "black" : "red",
            color: "white",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CourseProctorForm;
